<template>
    <div class="todo-page flex flex-col flex-grow justify-center container">
        <Toast position="top-center" />
        <div class="page-content mb-5">
            <h2 class="page-title mb-4" v-if="application" aria-busy="false">
                {{ $t(`${application.applicationType.resourceKey}`) }} -
                {{ $t(`lib_pending_actions`) }}
                <Badge
                    v-if="todos && todos.length && pendingActionsLength"
                    class="ms-2 align-text-top"
                    :value="pendingActionsLength"
                ></Badge>
            </h2>
            <div class="row" v-if="!loading && todos && todos.length" aria-busy="false">
                <div class="col-sm-12">
                    <div
                        class="card todo-card mb-3"
                        v-for="(todo, index) of todos"
                        :key="index"
                    >
                        <template v-if="todo.type !== 'OFFER'">
                            <div class="card-header d-flex align-items-center">
                                <h4 class="card-title mb-0 me-3">
                                    {{ $t(`${todo.name}`) }}
                                    <span v-if="!todo.isRequired"
                                        >({{ $t('lib_optional') }})</span
                                    >
                                </h4>
                                <div
                                    class="todo-status py-1 text-uppercase fw-bold"
                                    :class="todo.status"
                                >
                                    <span
                                        class="todo-status-text"
                                        :class="todo.status"
                                    >
                                        <font-awesome-icon class="todo-status-icon me-2 fs-4" :class="todo.status" :icon="applicationStatusIcon(todo.status )" />
                                        {{
                                            $t(
                                                `st_${todo.status.toLowerCase()}`
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                            <div class="card-body">
                                <p
                                    v-if="todo.description"
                                    v-html="$t(`${todo.description}`)"
                                    class="mb-3"
                                ></p>
                                <p
                                    v-if="todo.dueDate"
                                    class="mb-3"
                                    :class="{
                                        'text-danger': dueDateUnderAWeek(
                                            todo.dueDate
                                        ),
                                    }"
                                >
                                    <span
                                        v-tooltip.top.hover.focus="
                                            $t('lib_due')
                                        "
                                        class="me-1 fw-bold"
                                    >
                                        <font-awesome-icon icon="fad fa-calendar-alt"/>
                                        {{ $t('lib_due') }}:
                                    </span>
                                    {{ formatDate(todo.dueDate) }}
                                </p>
                                <div
                                    v-if="todo.isEdit"
                                    class="document-uploader-wrapper"
                                >
                                    <div
                                        class="document-info-disclaimer mt-3 d-flex align-items-center"
                                    >
                                        <font-awesome-icon icon="fas fa-info-circle" class="fs-3 text-primary me-2" />
                                        <div class="document-disclaimer-text">
                                            <p class="mb-0">
                                                {{ $t('lib_upload_info') }}
                                            </p>
                                            <p class="mb-0">
                                                {{ $t('lib_doc_rules') }}
                                            </p>
                                        </div>
                                    </div>
                                    <FileUpload
                                        name="demo[]"
                                        :showUploadButton="false"
                                        :showCancelButton="false"
                                        :fileLimit="1"
                                        :customUpload="true"
                                        :maxFileSize="10000000"
                                        @uploader="
                                            uploadDoc($event, todo, index)
                                        "
                                        @select="uploadDoc($event, todo, index)"
                                        accept="image/jpeg, image/jpg, application/pdf"
                                        :ref="`fileUpload-${index}`"
                                    >
                                        <template #empty>
                                            <p>
                                                <font-awesome-icon icon="far fa-cloud-upload" class="me-1 fs-3"/>
                                                {{ $t(`lib_drop`) }}

                                                <button
                                                    @click="
                                                        selectDocument(index)
                                                    "
                                                    class="btn btn-link p-0"
                                                >
                                                    {{ $t(`lib_browse_doc`) }}
                                                </button>
                                            </p>
                                        </template>
                                    </FileUpload>                                   
                                </div>
                                <div v-else class="uploaded-document-wrapper">
                                    <div
                                        class="uploaded-document mb-3 d-flex align-items-center"
                                    >
                                        <Avatar
                                            shape="circle"
                                            size="large"
                                            class="bg-light border document-icon me-3 p-3"
                                            v-tooltip.top.hover.focus="
                                                $t('lib_doc_download')
                                            "
                                            :aria-label="$t('lib_doc_download')"
                                            :icon="
                                                fileIcon(
                                                    todo?.document?.extension
                                                )
                                            "
                                        />
                                        <div class="document-details me-md-5">
                                            <p
                                                class="fw-bold mb-0 text-uppercase document-name"
                                                v-tooltip.top.hover="
                                                    $t('lib_doc_download')
                                                "
                                                @click="downloadDoc(todo)"
                                            >
                                                {{ todo?.document?.name }}
                                            </p>
                                            <p
                                                v-tooltip.top.hover="
                                                    $t('lib_upload_date')
                                                "
                                                class="mb-0 small fst-italic text-muted"
                                            >
                                                {{
                                                    formatDate(
                                                        todo?.document
                                                            ?.uploadDate
                                                    )
                                                }}
                                            </p>
                                        </div>
                                        
                                        <button
                                            type="button"
                                            class="replace-documents-btn btn btn-text px-6 py-1 ms-auto ms-sm-0"
                                            @click="
                                                deleteDoc(
                                                    $event,
                                                    todo?.document?.id
                                                )
                                            "
                                            v-tooltip.top.hover.focus="$t('lib_remove_doc')"
                                        >
                                            <font-awesome-icon icon="fas fa-trash"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <!-- OFFER CARD -->
                            <div class="card-header d-flex align-items-center">
                                <h4 class="card-title mb-0 me-3">
                                    {{ $t(`lib_offer_title`) }}
                                </h4>
                                <div
                                    class="todo-status py-1 text-uppercase fw-bold"
                                    :class="todo.status"
                                >
                                    <span
                                        class="todo-status-text"
                                        :class="todo.status"
                                    >
                                        <font-awesome-icon class="todo-status-icon me-2 fs-4" :class="todo.status" :icon="applicationStatusIcon(todo.status)" />
                                        {{
                                            $t(
                                                `st_${todo.status.toLowerCase()}`
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                            <div class="card-body">
                                <h4 class="mb-3 text-center">
                                    {{ $t(`lib_offer_desc`) }}
                                    <b>{{ unitLabel(todo.unitId) }}</b>
                                </h4>
                                <p
                                    v-if="todo.dueDate"
                                    class="mb-3 text-center"
                                    :class="{
                                        'text-danger': dueDateUnderAWeek(
                                            todo.dueDate
                                        ),
                                    }"
                                >
                                    <span
                                        v-tooltip.top.hover.focus="
                                            $t('lib_due')
                                        "
                                        class="me-1 fw-bold"
                                    >
                                        <font-awesome-icon icon="fad fa-calendar-alt"/>
                                        {{ $t('lib_due') }}:
                                    </span>
                                    {{ formatDate(todo.dueDate) }}
                                </p>
                                <div class="keyboard-type-picker">
                                    <p class="keyboard-type-description">
                                        {{ $t('lib_keyboard') }}
                                    </p>
                                    <div class="keyboard-type-card-wrapper">
                                        <div class="keyboard-type-card">
                                            <div class="keyboard-radio-btn">
                                                <RadioButton
                                                    name="keyboardType"
                                                    value="azerty"
                                                    id="azerty"
                                                    v-model="keyboardType"
                                                />
                                                <label
                                                    for="azerty"
                                                    class="ms-2"
                                                >
                                                    AZERTY
                                                </label>
                                            </div>
                                            <img
                                                @click="keyboardType = 'azerty'"
                                                src="../assets/images/azerty-keyboard.png"
                                                alt="AZERTY keyboard illustration"
                                                class="img-fluid"
                                                aria-role="button"
                                            />
                                        </div>
                                        <div class="keyboard-type-card">
                                            <div class="keyboard-radio-btn">
                                                <RadioButton
                                                    name="keyboardType"
                                                    value="qwerty"
                                                    id="qwerty"
                                                    v-model="keyboardType"
                                                />
                                                <label
                                                    for="qwerty"
                                                    class="ms-2"
                                                >
                                                    QWERTY
                                                </label>
                                            </div>
                                            <img
                                                @click="keyboardType = 'qwerty'"
                                                src="../assets/images/qwerty-keyboard.png"
                                                alt="QWERTY keyboard illustration"
                                                class="img-fluid"
                                                aria-role="button"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body" v-if="isShortOnEESC">
                                    <Checkbox
                                        id="shortGrantPaymentRefusal"
                                        v-model="shortGrantPaymentRefusal"
                                        :binary="true"
                                    />
                                    <label class="ms-2" for="shortGrantPaymentRefusal">
                                        {{ $t('short_grant_payment_refusal') }}
                                    </label>
                                </div>
                                <div class="card-actions">
                                    <button
                                        type="button"
                                        @click="onDecline($event)"
                                        :disabled="loading"
                                        class="decline-offer-btn btn btn-lg btn-outline-danger px-5 py-1"
                                    >
                                        {{ $t('btn_decline') }}
                                    </button>
                                    <div
                                        v-if="!keyboardType"
                                        v-tooltip.top.hover.focus="
                                            $t('lib_keyboard')
                                        "
                                    >
                                        <button
                                            type="button"
                                            :disabled="loading || !keyboardType"
                                            class="accept-offer-btn btn btn-lg btn-success px-5 py-1"
                                        >
                                            {{ $t('btn_accept') }}
                                        </button>
                                    </div>
                                    <button
                                        v-else
                                        type="button"
                                        @click="onAccept()"
                                        :disabled="loading || !keyboardType"
                                        class="accept-offer-btn btn btn-lg btn-success px-5 py-1"
                                    >
                                        {{ $t('btn_accept') }}
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
            <div
                v-if="!loading && todos && !todos.length"
                class="todos-complete mb-5"
                aria-busy="false"
            >
                <h1>
                    {{ $t('lib_tasks_empty') }}
                </h1>
                <h3>
                    {{ $t('lib_tasks_done') }}
                </h3>
                <img
                    src="../assets/images/completed-task.png"
                    alt="Clipboard with checkmark"
                />
            </div>
            <div v-if="loading" class="card view-application-skeleton mb-5" aria-busy="true">
                <div class="card-header d-flex justify-content-between py-3">
                    <Skeleton width="12rem"></Skeleton>
                    <Skeleton width="8rem"></Skeleton>
                </div>
                <div class="card-body d-flex justify-content-between flex-wrap">
                    <div>
                        <Skeleton width="10rem" class="mb-2"></Skeleton>
                        <Skeleton width="5rem" class="mb-2"></Skeleton>
                        <Skeleton width="7rem" class="mb-2"></Skeleton>
                        <Skeleton width="8rem" class="mb-2"></Skeleton>
                    </div>
                    <div>
                        <Skeleton
                            width="14rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                        <Skeleton
                            width="12rem"
                            class="ms-md-auto mb-2"
                        ></Skeleton>
                        <div class="d-flex justify-content-between flex-wrap">
                            <Skeleton
                                width="6rem"
                                height="2rem"
                                class="ms-md-auto mb-2"
                            ></Skeleton>
                            <Skeleton
                                width="6rem"
                                height="2rem"
                                class="ms-md-auto mb-2"
                            ></Skeleton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ConfirmPopup></ConfirmPopup>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Toast from 'primevue/toast';
import Skeleton from 'primevue/skeleton';
import FileUpload from 'primevue/fileupload';
import Badge from 'primevue/badge';
import Avatar from 'primevue/avatar';
import ConfirmPopup from 'primevue/confirmpopup';
import RadioButton from 'primevue/radiobutton';
import CommonMixins from '../mixins';
import Checkbox from 'primevue/checkbox';
import { add, isBefore, parseISO } from 'date-fns';

export default {
    name: 'TodoPage',
    components: {
        Toast,
        Skeleton,
        FileUpload,
        Badge,
        Avatar,
        ConfirmPopup,
        RadioButton,
        Checkbox,
    },
    data() {
        return {
            loading: false,
            application: null,
            applicationId: null,
            todos: null,
            keyboardType: null,
            shortGrantPaymentRefusal: false,
        };
    },
    computed: {
        ...mapGetters([
            'countryLabel',
            'languageLabel',
            'studyFieldLabel',
            'unitLabel',
            'europeanInstitutionsLabel',
            'isEESCSite',
        ]),
        pendingActionsLength() {
            return this.todos.filter((todo) => todo.status === 'PENDING')
                .length;
        },
        isShortOnEESC() {
            return (
                this.application &&
                this.application.applicationType &&
                this.application.applicationType.code === 'SHORT' &&
                this.isEESCSite
            );
        },
    },
    methods: {
        ...mapActions([
            'loadAppData',
            'loadTasks',
            'uploadDocument',
            'downloadDocument',
            'deleteDocument',
            'completeTask',
            'loadApplicationData',
            'loadApplications',
            'acceptOffer',
            'declineOffer',
            'redirectToDashboard',
        ]),
        dueDateUnderAWeek(dateToCompare) {
            const currentWeek = add(new Date(), {
                days: 7,
            });
            return !isBefore(currentWeek, parseISO(dateToCompare));
        },
        applicationStatusIcon(statusCode) {
            let statusIcon;
            switch (statusCode) {
                case 'COMPLETED':
                    statusIcon = 'fal fa-check-circle';
                    break;
                default:
                    statusIcon = 'fal fa-clipboard-list-check';
                    break;
            }
            return statusIcon;
        },
        fileIcon(extension) {
            let fileIcon;
            switch (extension) {
                case '.pdf':
                    fileIcon = 'pi pi-file-pdf';
                    break;
                case '.jpg':
                case '.jpeg':
                    fileIcon = 'pi pi-image';
                    break;
                default:
                    fileIcon = 'pi pi-file';
                    break;
            }
            return fileIcon;
        },
        selectDocument(index) {
            var fileUpload = this.$refs[`fileUpload-${index}`][0];
            fileUpload.choose();
        },
        uploadDoc(event, todo, index) {
            const formData = new FormData();
            formData.append('file', event.files[0]);
            formData.append('taskId', todo.id);

            this.uploadDocument(formData).then(
                () => {
                    this.$toast.add({
                        severity: 'success',
                        detail: this.$t('lib_action_success', [
                            this.$t('lib_doc_upload'),
                        ]), // "Document uploaded succesfully!",
                        life: 3000,
                    });
                    this.loadPageData();
                },
                (err) => {
                    console.error(err);
                    var fileUpload = this.$refs[`fileUpload-${index}`][0];
                    fileUpload.clear();
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: err.detail
                            ? this.$t(err.detail)
                            : `${this.$t('lib_action_failed', [
                                  this.$t('lib_doc_upload'),
                              ])}`, // "There was a problem uploading your file, please try again later.",
                        life: 3000,
                    });
                }
            );
        },
        downloadDoc(todo) {
            // this methods refers to the event triggered by the download btn;
            const todoId = todo?.document?.id;
            this.downloadDocument(todoId).then(
                (res) => {
                    this.downloadFile(res, todo.document.name);
                    this.$toast.add({
                        severity: 'success',
                        detail: this.$t('lib_action_success', [
                            this.$t('lib_doc_download'),
                        ]), //"Document downloaded succesfully!",
                        life: 3000,
                    });
                },
                (err) => {
                    console.error(err);
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('lib_action_failed', [
                            this.$t('lib_doc_download'),
                        ]), //"There was a problem downloading your file, please try again later."
                        life: 3000,
                    });
                }
            );
        },
        downloadFile(res, name) {
            // this method refers to the event of downloading the file after we recieve the response from the api;
            let fileBlob = window.URL.createObjectURL(new Blob([res.data])),
                virtualFile = document.createElement('a');

            virtualFile.href = fileBlob;
            virtualFile.setAttribute('download', name);
            document.body.appendChild(virtualFile);
            virtualFile.click();
        },
        deleteDoc(event, docId) {
            console.log('eve');
            this.$confirm.require({
                target: event.currentTarget,
                message: this.$t('status_incomplete_alert'), //"Are you sure you want to proceed?",
                icon: 'pi pi-exclamation-triangle text-warning',
                accept: () => {
                    //callback to execute when user confirms the action
                    this.deleteDocument(docId).then(
                        () => {
                            this.loadPageData();
                            this.$toast.add({
                                severity: 'success',
                                // TODO - lib_action_success The {0} action completed successfully.
                                detail: this.$t('lib_action_success', [
                                    this.$t('lib_doc_remove'),
                                ]), //"Document deleted succesfully!",
                                life: 3000,
                            });
                        },
                        (err) => {
                            console.error(err);
                            // here should be error notification;
                            this.$toast.add({
                                severity: 'error',
                                // TODO - lib_action_failed The {0} action completed successfully.

                                detail: this.$t('lib_action_failed', [
                                    this.$t('lib_doc_remove'),
                                ]), // "There was a problem uploading your file, please try again later.",
                                life: 3000,
                            });
                        }
                    );
                },
            });
        },
        completeTodo(todo) {
            // Event for complete method here;
            this.completeTask(todo);
        },
        loadPageData() {
            this.loading = true;
            this.loadTasks(this.applicationId).then(
                (res) => {
                    this.loading = false;
                    this.todos = res.data.map((todo) => {
                        return {
                            ...todo,
                            isEdit: !todo.document,
                        };
                    });
                },
                (err) => {
                    console.error(err);
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('lib_tasks_error'), // "The system could not load your pending actions. Please try again later",
                        life: 3000,
                    });
                }
            );
        },
        onAccept() {
            this.loading = true;
            this.acceptOffer({
                applicationId: this.applicationId,
                keyboardType: this.keyboardType,
                shortGrantPaymentRefusal: this.shortGrantPaymentRefusal
            }).then(
                () => {
                    this.$toast.add({
                        severity: 'success',
                        detail: this.$t('lib_action_success', [
                            this.$t('btn_accept'),
                        ]),
                        life: 3000,
                    });
                    setTimeout(() => {
                        this.loading = false;
                        this.loadPageData();
                    }, 5000);
                },
                (err) => {
                    console.error(err);
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('lib_action_failed', [
                            this.$t('btn_accept'),
                        ]),
                        life: 3000,
                    });
                    this.loading = false;
                }
            );
        },
        onDecline(event) {
            console.log('eve');
            this.$confirm.require({
                target: event.currentTarget,
                message: this.$t('lib_decline_check'),
                icon: 'pi pi-exclamation-triangle text-warning',
                accept: () => {
                    //callback to execute when user confirms the action
                    this.loading = true;
                    this.declineOffer(this.applicationId).then(
                        () => {
                            this.$toast.add({
                                severity: 'success',
                                // TODO - lib_action_success The {0} action completed successfully.
                                detail: this.$t('lib_action_success', [
                                    this.$t('btn_decline'),
                                ]), //"Document deleted succesfully!",
                                life: 3000,
                            });
                            setTimeout(() => {
                                // Redirect the user to homepage after he declines the offer - in this case the application is deleted;
                                this.redirectToDashboard();
                            }, 3000);
                        },
                        (err) => {
                            console.error(err);
                            // here should be error notification;
                            this.$toast.add({
                                severity: 'error',
                                // TODO - lib_action_failed The {0} action completed successfully.

                                detail: this.$t('lib_action_failed', [
                                    this.$t('btn_decline'),
                                ]), // "There was a problem uploading your file, please try again later.",
                                life: 3000,
                            });
                            this.loading = false;
                        }
                    );
                },
            });
        },
    },
    created() {
        this.loadAppData();
    },
    mounted() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('applicationId')) {
            this.applicationId = urlParams.get('applicationId');
            this.loadApplications().then((res) => {
                this.application = res.data.find(
                    (app) => app.applicationId == this.applicationId
                );
            });
            this.loadPageData();
        } else {
            this.$toast.add({
                severity: 'error',
                detail: this.$t('lib_application_error'), // "Application not found! You will be redirected to Dashboard",
                life: 3000,
            });
            history.back();
        }
    },
    mixins: [CommonMixins],
};
</script>

<style lang="scss">
.document-info-disclaimer{
    .fa-info-circle{
        color: #2C4390;
    }

    .document-disclaimer-text{
        color: #000;
    }
}
</style>
